import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://vktours.com';
const BRAND = 'VK Tours';
const DEFAULT_OG = 'https://www.vktours.com/images/vk-tours-logo.png';

/**
 * Universal SEO component for any page.
 * Props:
 *   title       — page-specific title (will be suffixed with brand)
 *   description — meta description (max 160 chars ideal)
 *   image       — Open Graph image URL (full HTTPS URL)
 *   path        — pathname for canonical (e.g. "/destination/bali")
 *   type        — "website" | "article" | "product"
 *   noindex     — set true for admin/auth pages
 *   jsonLd      — optional Schema.org structured data object
 */
const SEO = ({
  title,
  description = 'Book customised holiday tour packages with VK Tours — Bali, Maldives, Dubai, Thailand, Kerala, Kashmir & 100+ destinations. Best price guarantee.',
  image = DEFAULT_OG,
  path = '',
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title ? `${title} | ${BRAND}` : `${BRAND} — Book Best Holiday Packages`;
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`.replace(/\/$/, '') || BASE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description.slice(0, 180)} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description.slice(0, 180)} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description.slice(0, 180)} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
